import { z } from 'zod';
import { statementImportBatchResultSchema } from './bank.js';
import { centAmount, isoDate, uuid } from './posting.js';
/**
 * Die Verträge des FinTS-Zugangs (Spec Bankanbindung Stufe 2, Abschnitt 6.1 und
 * 10; Plan-Nachtrag „Weg A" vom 2026-08-24).
 *
 * **Eine eigene Datei und nicht `bank.ts`.** Zwei Gründe, und der zweite wiegt
 * schwerer: Der Zugang ist eine **technische Zugangsart** und kein Geldkonto —
 * `bank.ts` beschreibt Konten, Umsätze, Auszüge und Deckung. Und `bank.ts` ist
 * eine Datei, an der die Aufgaben `F-tan` und `F-abruf` derselben Ära weiter
 * arbeiten; zwei Agenten in derselben Datei sind die Kollision aus
 * `AGENTS-DISCIPLINE.md` 2b.
 *
 * **Der Vertrag trennt zwei Richtungen, und die Trennung ist die Zusicherung:**
 *
 * - **Hinein** (`fintsZugangEingabeSchema`) darf eine PIN. Sie ist das einzige
 *   Feld dieses Pakets, das ein Geheimnis trägt.
 * - **Heraus** (`fintsZugangSchema`) darf sie NICHT — und zwar weder im Klartext
 *   noch als Geheimtext. Das Ausgabeschema kennt kein Feld dafür; es kennt nur
 *   `pinHinterlegt: boolean`. Ein Feld, das es nicht gibt, kann kein Dienst
 *   versehentlich füllen (`E-313`, `E-319`).
 */
/**
 * Die Adresse des Instituts.
 *
 * **`https` und nichts anderes.** FinTS über PIN/TAN verschlüsselt seinen
 * Umschlag nicht selbst — das Segment `HNVSD` ist bei PIN/TAN ein Umschlag ohne
 * Verschlüsselung, und die PIN steht im Klartext in `HNSHA` jeder signierten
 * Nachricht (Spec 3.7). **Die Vertraulichkeit liefert TLS und nichts sonst.**
 * Ein `http` in dieser Spalte gäbe die PIN auf dem Draht preis, und der Fehler
 * wäre nicht zu sehen: Der Abruf funktionierte.
 */
export const fintsUrl = z
    .string()
    .trim()
    .min(1, 'Adresse fehlt')
    .max(500)
    .url('Keine gültige Adresse')
    .refine((wert) => wert.toLowerCase().startsWith('https://'), {
    message: 'Die Adresse muss https sein — ohne TLS steht die PIN im Klartext auf dem Draht.',
});
/**
 * Die Bankleitzahl, wie sie ins Segment `HKIDN` geht.
 *
 * Genau acht Ziffern, als Zeichenkette. Eine Zahl verlöre die führende Null,
 * und die Bankleitzahl `01070024` gibt es.
 */
export const bankleitzahl = z
    .string()
    .trim()
    .regex(/^[0-9]{8}$/, 'Die Bankleitzahl hat genau acht Ziffern');
/**
 * Die Bank-PIN — **das einzige Feld dieses Pakets, das ein Geheimnis trägt**.
 *
 * **Kein `.trim()`.** Bei jedem anderen Feld ist das Beschneiden eine
 * Freundlichkeit; hier wäre es eine stille Änderung eines Geheimnisses. Eine PIN
 * mit einem Leerzeichen am Ende ist eine andere PIN, und wenn das Institut sie
 * ablehnt, soll der Fehler dort auffallen und nicht in einem Vertrag.
 *
 * **Keine Formprüfung über die Länge hinaus.** Was eine gültige PIN ist,
 * entscheidet das Institut; eine Regel wie „fünf Ziffern" wäre geraten und
 * wiese einen gültigen Zugang ab. Die Obergrenze steht gegen einen Rumpf, den
 * niemand tippt.
 */
export const bankPin = z.string().min(1, 'PIN fehlt').max(100);
/**
 * Was beim Einrichten oder Ändern hereinkommt (`PUT /bank-accounts/:id/fints`).
 *
 * **Die PIN ist optional, und das ist die Stufengrenze.** Stufe 2a gibt sie je
 * Abruf ein und hinterlegt sie nicht (`D-10`); hinterlegt wird sie für den
 * nächtlichen Abruf der Stufe 2b, den die Ruling `FO-1` vom 2026-08-24 erlaubt
 * hat. Ein `PUT` ohne `pin` lässt eine bereits hinterlegte **unverändert** —
 * wer sie entfernen will, sagt das ausdrücklich mit `pinEntfernen`.
 *
 * **`pinEntfernen` und nicht `pin: null`.** Ein `null` in einem Feld, das sonst
 * ein Geheimnis trägt, sieht in jedem Protokoll und in jeder Maske wie ein
 * vergessenes Feld aus. Ein eigenes, benanntes Feld sagt, was gemeint ist.
 */
export const fintsZugangEingabeSchema = z
    .object({
    url: fintsUrl,
    blz: bankleitzahl,
    benutzerkennung: z.string().trim().min(1, 'Benutzerkennung fehlt').max(200),
    kundenkennung: z.string().trim().min(1).max(200).optional(),
    tanVerfahrenId: z.number().int().positive().optional(),
    tanMedium: z.string().trim().min(1).max(200).optional(),
    pin: bankPin.optional(),
    pinEntfernen: z.boolean().optional(),
})
    .strict()
    .refine((wert) => !(wert.pin !== undefined && wert.pinEntfernen === true), {
    message: 'Eine PIN hinterlegen und sie zugleich entfernen ist kein Vorgang.',
    path: ['pinEntfernen'],
});
/**
 * Wie der letzte Lauf der **täglichen FinTS-Schiene** ausgegangen ist
 * (`F-fintstakt`, Migration `0097`, Spalte `fints_zugang.takt_befund`).
 *
 * **Drei Werte und ausdrücklich nicht zwei.** `freigabe_faellig` neben `fehler`
 * zu führen ist der ganze Punkt: Verlangt das Institut eine starke
 * Authentifizierung, ist der Lauf **nicht gescheitert** — es ist nur niemand da,
 * der freigeben könnte (Spec 8.3). Wer beide zusammenlegte, meldete dem
 * Betreiber einen Betriebsfehler, wo in Wahrheit ein Mensch einmal auf einen
 * Knopf drücken muss.
 *
 * **Ein eigenes, benanntes Schema und keine `z.enum(...)`-Liste an der
 * Verwendungsstelle.** Die Oberfläche spiegelt diesen Vertrag (`schemas.ts`),
 * und eine abgeschriebene Werteliste wäre die zweite Wahrheit, die eines Tages
 * auseinanderläuft — dieselbe Regel, die die ESLint-Schranke
 * `no-restricted-syntax` für jede andere Werteliste dieses Pakets erzwingt.
 */
export const fintsTaktBefundSchema = z.enum(['ok', 'freigabe_faellig', 'fehler']);
/**
 * Ein TAN-Verfahren aus der zwischengespeicherten BPD des Zugangs
 * (`fints_zugang.bankdaten`, `D-12`) — die Kennung, mit der ein Mensch das
 * Freischaltschreiben der Bank abgleicht (`F-tanliste`, 2026-08-30).
 *
 * **Nicht dieselbe Form wie `fintsZugangstestVerfahrenSchema`.** Jenes Feld
 * kommt aus einem gerade laufenden Prüfdialog und kennt den Sonderfall einer
 * abgelehnten Anmeldung, bei der nur die Kennzahl ankommt (`name`/`art` dann
 * `null`). Diese Liste hier stammt aus einer bereits **erfolgreich**
 * abgeschlossenen BPD — `name` und `istEntkoppelt` sind deshalb nie fraglich
 * belegt.
 */
export const fintsZugangTanVerfahrenSchema = z
    .object({
    id: z.number().int(),
    name: z.string(),
    istEntkoppelt: z.boolean(),
    /** Die Namen der für dieses Verfahren freigeschalteten TAN-Medien, wörtlich wie die Bank sie führt. */
    medien: z.array(z.string()),
})
    .strict();
/**
 * Der Zustand des Zugangs (`GET /bank-accounts/:id/fints`).
 *
 * **Ein Verbund über `eingerichtet` und kein Objekt mit lauter `null`.** Ein
 * nicht eingerichteter Zugang ist **kein Fehler** — er ist der Anfangszustand
 * jedes Bankkontos. Eine 404 dafür machte aus „noch nicht eingerichtet" ein
 * „gibt es nicht", und die Maske müsste zwischen zwei Fehlern unterscheiden, um
 * einen Knopf zu zeichnen.
 *
 * **Kein Feld für die PIN, in keiner der beiden Gestalten.** `pinHinterlegt`
 * sagt, ob eine da ist; mehr geht nach draussen nicht (`E-319`).
 */
export const fintsZugangSchema = z.discriminatedUnion('eingerichtet', [
    z.object({ eingerichtet: z.literal(false) }).strict(),
    z
        .object({
        eingerichtet: z.literal(true),
        url: z.string(),
        blz: z.string(),
        benutzerkennung: z.string(),
        kundenkennung: z.string().nullable(),
        tanVerfahrenId: z.number().int().nullable(),
        tanMedium: z.string().nullable(),
        maxTage: z.number().int().nullable(),
        /** Ob eine PIN hinterlegt ist — **nicht**, welche. */
        pinHinterlegt: z.boolean(),
        pinHinterlegtAm: z.string().nullable(),
        letzteStarkeAuth: z.string().nullable(),
        letzterAbruf: z.string().nullable(),
        /**
         * Die **Vorschau** aus Spec 8.3 — `letzte_starke_auth` plus neunzig Tage.
         *
         * **Sie ist eine Vorschau und niemals eine Sperre.** Autorität ist die
         * Antwort des Instituts am Abruf (`D-15`): Fordert es eine TAN, gibt es
         * eine TAN-Aufforderung. Ein System, das den Termin rechnet und daraufhin
         * einen Abruf unterlässt, erfindet eine Sperre, die die Bank nicht gesetzt
         * hat — und lässt einen Tag ungedeckt, den es hätte holen können.
         */
        auffrischungVoraussichtlichAm: z.string().nullable(),
        /** Tage bis dahin; negativ, wenn der Zeitpunkt vorbei ist. */
        tageBisAuffrischung: z.number().int().nullable(),
        /**
         * Wann die **tägliche FinTS-Schiene** des Hintergrundlaufs zuletzt einen
         * Abruf begonnen hat (`F-fintstakt`, Migration `0097`).
         *
         * **Begonnen und nicht beendet.** Der Zeitpunkt wird vor dem Dialog
         * geschrieben, damit ein abgestürzter Lauf keinen zweiten Anmeldeversuch
         * beim Institut auslöst — Institute sperren nach drei Fehlversuchen.
         * `null` heisst «die Schiene hat diesen Zugang noch nie angefasst»: Sie
         * fasst nur Zugänge mit **hinterlegter** PIN an, und das ist in Stufe 2a
         * nicht der Regelfall.
         */
        taktLetzterVersuch: z.string().nullable(),
        /**
         * Wie der letzte Lauf der Schiene ausgegangen ist.
         *
         * **`freigabe_faellig` ist kein Fehler**, sondern eine Aufforderung an
         * einen Menschen: Das Institut verlangt eine starke Authentifizierung,
         * und im Hintergrundlauf ist niemand, der sie geben könnte (Spec 8.3).
         * Erneuert wird sie über den Abruf von Hand auf dieser Seite; bis dahin
         * bleibt der Zeitraum **ungedeckt** statt leer.
         */
        taktBefund: fintsTaktBefundSchema.nullable(),
        /** Die kurze, geschwärzte Meldung zum Befund. `null` bei `ok`. */
        taktMeldung: z.string().nullable(),
        /**
         * Die dem Institut bekannten TAN-Verfahren — aus der
         * zwischengespeicherten BPD des **letzten erfolgreichen** Dialogs dieses
         * Zugangs (`D-12`; Abruf, Zugangstest oder Medienabruf legen sie
         * gleichermassen ab, siehe `bankdatenSichern`). Rein lesend: Diese Liste
         * kostet keinen Bankkontakt.
         *
         * **`null` heisst „noch kein erfolgreicher Dialog"**, nicht „das
         * Institut bietet nichts an" — dieselbe Unterscheidung wie bei
         * `letzteStarkeAuth`. Eine **leere** Liste bei vorhandener BPD ist
         * ehrlich möglich und bleibt eine leere Liste.
         */
        bekannteTanVerfahren: z.array(fintsZugangTanVerfahrenSchema).nullable(),
    })
        .strict(),
]);
// ===========================================================================
// Der Abruflauf (Spec 4.2, 4.3, 10; Plan-Aufgabe 11)
// ===========================================================================
/**
 * Was `POST /bank-accounts/:id/fints/abruf` entgegennimmt.
 *
 * **Nur die PIN, und sie ist Pflicht.** Stufe 2a gibt sie je Abruf ein
 * (`D-10`); die hinterlegte PIN aus `fints_zugang.pin_enc` ist die Grundlage
 * für den Lauf **ohne** Menschen (Stufe 2b) und ersetzt die Eingabe hier
 * ausdrücklich nicht (Plan-Nachtrag vom 2026-08-24, „Was dieser Nachtrag NICHT
 * ändert").
 *
 * **Kein Fenster, kein Stichtag, keine Kontonummer im Rumpf.** Das Fenster
 * rechnet der Abruf aus dem Bestand, der Zuständigkeitsgrenze und `maxDays`
 * (Spec 5.1); ein Fenster aus der Anfrage wäre ein zweiter Weg, auf dem jemand
 * einen Zeitraum überspringen kann, und der Saldoabgleich prüfte danach eine
 * Lücke, die er selbst erlaubt hat.
 */
export const fintsAbrufStartSchema = z.object({ pin: bankPin }).strict();
/**
 * Was `POST /bank-accounts/:id/fints/abruf/:laufId/tan` entgegennimmt.
 *
 * **Der Code ist optional, und das ist kein Versehen.** Zwei Fälle laufen über
 * denselben Endpunkt, weil sie in der Bibliothek derselbe Aufruf sind
 * (`getAccountStatementsWithTan(referenz, tan?)`):
 *
 * - **Mit `tan`:** ein eingetippter Code (chipTAN, smsTAN).
 * - **Ohne `tan`:** „ich habe in der App freigegeben, frag jetzt nach" — die
 *   Handbestätigung eines entkoppelten Verfahrens (Spec 4.2). Sie ist kein
 *   Ersatz für die Freigabe, sondern nur der Verzicht auf das Warten bis zur
 *   nächsten planmässigen Statusabfrage.
 */
export const fintsTanSchema = z
    .object({ tan: z.string().trim().min(1, 'TAN fehlt').max(20).optional() })
    .strict();
/**
 * Die vier Zustände eines Laufs (Spec 4.3).
 *
 * `wartet_auf_freigabe` ist ein **eigener** Zustand und nicht ein `laeuft` mit
 * gesetzter Aufforderung: Die Oberfläche zeigt dort etwas völlig anderes — den
 * Wortlaut des Instituts und einen Fortschritt —, und ein Zustand, den man aus
 * der Belegung eines anderen Feldes erschliessen muss, wird irgendwo falsch
 * erschlossen.
 */
export const fintsLaufZustandSchema = z.enum([
    'laeuft',
    'wartet_auf_freigabe',
    'fertig',
    'abgebrochen',
]);
/**
 * Wie die Freigabe dieses Laufs eingeholt wird.
 *
 * `entkoppelt` heisst: Der Lauf fragt von selbst nach, in den Abständen der
 * Bank. `code` heisst: Er wartet auf `POST …/tan`. Welches von beidem gilt,
 * entscheidet das TAN-Verfahren des Zugangs und nicht die Oberfläche — sie
 * **liest** es hier, damit sie nicht raten muss, ob ein Eingabefeld zu zeichnen
 * ist.
 */
export const fintsFreigabeArtSchema = z.enum(['entkoppelt', 'code']);
/** Der Saldoabgleich, wie er im Bericht steht (Spec 7.2). */
export const fintsSaldoAbgleichSchema = z
    .object({
    stichtag: isoDate,
    waehrung: z.string(),
    gemeldet: centAmount,
    errechnet: centAmount,
    differenz: centAmount,
    richtung: z.enum(['gleich', 'unser_bestand_hoeher', 'unser_bestand_niedriger']),
    stimmt: z.boolean(),
})
    .strict();
/**
 * Der Bericht eines Abrufs — der Bericht der Stufe 1 je Dokument, um Fenster
 * und Saldoabgleich ergänzt (Spec 10, Ansicht 4).
 *
 * **`importe` trägt `statementImportBatchResultSchema` und damit kein
 * `storageKey`.** Der Pfad im Archiv des Betreibers bleibt innen — dieselbe
 * Regel wie beim Hochladen, und sie ist hier zusätzlich Teil von `E-313`: Was
 * der Lauf ablegt, ist bereits die Aussenform.
 */
export const fintsAbrufBerichtSchema = z
    .object({
    fenster: z.array(z.object({ von: isoDate, bis: isoDate }).strict()),
    fensterGekuerzt: z.boolean(),
    gewuenschterAnfang: isoDate,
    dokumente: z.number().int().nonnegative(),
    /**
     * In wie vielen Antwortnachrichten die Umsätze kamen (`E-2058`). Eins ist der
     * ungeteilte Regelfall; mehr heisst, das Institut hat die Antwort geteilt
     * (Rückmeldung `3040`) und jeder Teil wurde geholt. Ein Lauf, der vor dieser
     * Fassung abgelegt wurde, trägt das Feld nicht — die Zahl ist dann unbekannt,
     * nicht eins.
     */
    teile: z.number().int().nonnegative().optional(),
    importe: z.array(statementImportBatchResultSchema),
    saldo: fintsSaldoAbgleichSchema.nullable(),
    /** Warum kein Abgleich stattgefunden hat. `null`, wenn einer stattfand. */
    saldoGrund: z.string().nullable(),
    rueckmeldungen: z.array(z.object({ code: z.number().int(), text: z.string() }).strict()),
    /**
     * Hinweise zum Abruf, die kein Fehler sind — etwa ein Institut, dessen Umsätze nicht
     * in UTF-8 kamen und umgesetzt wurden (`E-2040`). Ein Lauf, der vor dieser Fassung
     * abgelegt wurde, trägt das Feld nicht und liest als leere Liste.
     */
    hinweise: z.array(z.string()).default([]),
    /**
     * Der Hinweis `fenster_leer`: Das Institut hat jedes abgefragte Fenster ohne
     * Umsatzsegment und mit der Rückmeldung `3010` „Es liegen keine Einträge vor"
     * zum Umsatzauftrag beantwortet — keine neuen Umsätze im Zeitraum, kein Fehler.
     * `code` ist der Rückmeldungscode der Bank. `null`, wenn Umsätze kamen oder
     * kein Fenster gefragt wurde; ein Lauf, der vor dieser Fassung abgelegt wurde,
     * trägt das Feld nicht und liest als `null`.
     */
    fensterLeer: z.object({ code: z.number().int() }).strict().nullable().default(null),
})
    .strict();
/**
 * Eine einzelne Rückmeldung des Instituts, wie sie im aufklappbaren
 * Detailblock der Oberfläche und im Änderungsprotokoll eines Abbruchs
 * erscheint (`F-diagnose2`, Spec 6.4).
 *
 * **`text` ist der Institutstext, geführt durch die Schwärzung** (`E-313`,
 * `E-335`) — er darf beim Verlassen des Dienstes kein Geheimnis mehr tragen.
 * Er bleibt trotzdem roher Bankwortlaut und gehört ausschliesslich in einen
 * als solchen beschrifteten Block, NIE in `fehler.meldung` (Spec 9).
 *
 * **`bezugssegment` ist der NAME des Anfragesegments** (`HKIDN`/`HKVVB`/
 * `HKTAN`/…), auf das sich die Rückmeldung bezieht — aufgelöst über die
 * Segmentnummer der eigenen Anfrage (`dialog.ts`, `refSegNr` aus dem
 * Antwortsegmentkopf, `lib-fints@1.5.0`). Fehlt das Feld, war der Bezug nicht
 * auflösbar — keine geratene Zuordnung.
 */
export const fintsBankRueckmeldungSchema = z
    .object({
    code: z.number().int(),
    text: z.string(),
    bezugssegment: z.string().optional(),
})
    .strict();
/**
 * Eine gewechselte FinTS-Nachricht, **geschwärzt** — der Drahtmitschnitt des
 * Zugangstests (`V-fintswire`, 2026-08-26).
 *
 * **Warum es dieses Feld gibt.** Ein Zugangstest, den das Institut mit `9955`
 * „Die Gerätebezeichnung ist unbekannt" abweist, sagt nichts darüber, **was**
 * gesendet wurde. Ob der hinterlegte Medienname im `HKTAN` ankommt, war bis
 * hierher Glaube und keine Messung; der Mitschnitt macht daraus eine Auskunft,
 * die ein Mensch am Bildschirm ablesen kann.
 *
 * **Warum es trotzdem kein Geheimnis trägt.** Der Text durchläuft zwei Lagen,
 * bevor er hier steht: die strukturelle Schwärzung des Drahts
 * (`schwaerzung.ts` — Produkt-ID aus `HKVVB`, PIN und TAN aus `HNSHA`,
 * Benutzer- und Kundenkennung gekürzt auf die letzten drei Zeichen) und danach
 * dieselbe Freitextschleuse, durch die auch {@link fintsBankRueckmeldungSchema}
 * geht. Es gibt keinen Schalter, der eine der beiden abstellt — der Draht
 * verlässt `dialog.ts` gar nicht anders (`E-313`, `E-316`, `E-359`).
 */
export const fintsDrahtNachrichtSchema = z
    .object({
    anfrage: z.string(),
    antwort: z.string(),
})
    .strict();
/**
 * Der Laufzustand, wie `GET /bank-accounts/:id/fints/abruf/:laufId` ihn ausgibt
 * (Spec 4.3).
 *
 * **Kein Feld dieses Schemas kann eine PIN oder die Produkt-ID tragen**, und
 * das ist die eine Hälfte von `E-313`, die diese Ära hier einlöst: Der Vertrag
 * ist `.strict()`, und ein Feld, das es nicht gibt, kann kein Dienst
 * versehentlich füllen. Die andere Hälfte — dass auch die **Fehlermeldung**
 * keins trägt — kann kein Schema zusagen; sie ist eine Eigenschaft des Laufs
 * und wird dort geprüft.
 */
export const fintsLaufSchema = z
    .object({
    laufId: uuid,
    bankAccountId: uuid,
    zustand: fintsLaufZustandSchema,
    /** Der Wortlaut des Instituts, unverändert (Spec 4.2, Schritt 3). */
    aufforderung: z.string().nullable(),
    /** Der Name des TAN-Mediums, zur Anzeige. */
    tanMedium: z.string().nullable(),
    /** Sekunden bis zur nächsten Statusabfrage — **aus den Angaben der Bank**. */
    naechsteFrageIn: z.number().int().nonnegative().nullable(),
    /** Die wievielte Freigabeaufforderung gerade läuft, ab 1. */
    freigabeRunde: z.number().int().positive().nullable(),
    /** Wie viele Runden das Institut zulässt (`maxStatusRequests`). */
    freigabeRundenMax: z.number().int().positive().nullable(),
    freigabeArt: fintsFreigabeArtSchema.nullable(),
    /**
     * Der hinterlegte Medienname, den das Institut **nicht anbietet** — das
     * getauschte Telefon (`V-fintsmedium`). Fehlt, solange nichts dergleichen
     * aufgetreten ist.
     *
     * Er steht hier, weil der Zugang unverändert richtig aussieht, während die
     * TAN auf einem anderen Gerät landet: Ohne diese Zeile sucht ein Mensch die
     * Ursache dort, wo sie nicht ist. **Geschwärzt** wie jedes Freitextfeld
     * dieses Vertrags (`E-313`).
     *
     * Eine **leere** Medienliste erzeugt dieses Feld ausdrücklich NICHT — sie
     * ist keine Auskunft der Bank, sondern deren Henne-Ei: `HITAB` kommt erst
     * nach einer erfolgreichen Anmeldung.
     */
    tanMediumNichtAngeboten: z.string().optional(),
    bericht: fintsAbrufBerichtSchema.nullable(),
    fehler: z
        .object({
        code: z.string(),
        meldung: z.string(),
        /**
         * Die Meldung des Wurfs, der hinter dem Code steckt — geschwärzt und
         * auf 500 Zeichen gekürzt (`FI-ursachekette`, `E-1425`; dieselbe Form
         * und dieselbe Begründung wie bei
         * `lastschriftEinreichungLaufSchema.fehler.ursache`,
         * `LS-einreichdiagnose`, `E-1349`).
         *
         * **Warum sie ein eigenes Feld ist und nicht an `meldung` hängt.**
         * `meldung` ist eine der sieben Meldungen aus Spec 9, wörtlich und
         * ohne angehängten Grund — die Zusage aus `E-313`. Was eine fremde
         * Bibliothek sagt, steht deshalb daneben und nicht darin; die
         * Oberfläche zeigt es unter der Meldung, nicht in ihr. `undefined`,
         * solange der Abbruch einen eigenen Code dieses Hauses ohne fremde
         * Ursache trug: Dann gibt es nichts danebenzustellen.
         *
         * **Der Stapelabzug steht ausdrücklich NICHT hier**, sondern nur im
         * Änderungsprotokoll: Er nennt Dateinamen und Zeilen dieses Programms
         * — eine Auskunft für den, der den Fehler behebt, und keine für den
         * Bildschirm dessen, der gerade Umsätze abrufen wollte.
         */
        ursache: z.string().optional(),
        /**
         * Die numerischen Rückmeldungscodes des Instituts, die zu diesem
         * Abbruch geführt haben — aus **allen** Runden, nicht nur der letzten
         * (Spec 6.4; Handbuch 3.190). Fehlt oder ist leer, wenn keine
         * bekannt sind (`F-diagnose`).
         *
         * **Nur Zahlen, nie Text des Instituts** — das bleibt `E-313`: Weder
         * die Bank-PIN noch die Produkt-ID lassen sich als Rückmeldungscode
         * ausdrücken, ein Code trägt kein Geheimnis.
         */
        bankCodes: z.array(z.number().int()).optional(),
        /**
         * Dieselben Rückmeldungen wie `bankCodes`, aber vollständig — Text
         * und, wenn auflösbar, Bezugssegment (`F-diagnose2`). Fehlt oder ist
         * leer, wenn keine bekannt sind. **Der Text ist geschwärzt** — dieselbe
         * Zusage wie bei `aufforderung` und `tanMedium` (`E-313`, `E-335`).
         */
        bankRueckmeldungen: z.array(fintsBankRueckmeldungSchema).optional(),
    })
        .strict()
        .nullable(),
    /**
     * Die Vorschau aus Spec 8.3, **nach** dem Lauf neu gelesen.
     *
     * Sie steht hier und nicht nur am Zugangsendpunkt, weil genau dieser Lauf
     * sie bewegt haben kann: Hat das Institut eine Freigabe verlangt und
     * bekommen, ist `letzte_starke_auth` neu gesetzt, und die Oberfläche soll
     * die neue Zahl zeigen, ohne einen zweiten Endpunkt zu fragen. `null`,
     * solange der Lauf nicht fertig ist oder nie eine starke Authentifizierung
     * stattgefunden hat.
     */
    auffrischung: z
        .object({
        letzteStarkeAuth: z.string(),
        voraussichtlichAm: z.string(),
        tageBisAuffrischung: z.number().int(),
    })
        .strict()
        .nullable(),
})
    .strict();
// ===========================================================================
// Die Institutssuche (Chip `F-institute`, 2026-08-25)
// ===========================================================================
/**
 * Eine Zeile der Institutsliste, wie `GET /bank/fints/institute` sie ausgibt.
 *
 * **Kein Geheimnis darin, und keins nötig.** BLZ, BIC, Institutsname, Ort und
 * die FinTS-Adresse sind öffentlich bekannte Angaben der Deutschen
 * Kreditwirtschaft — dieselbe Information, die `zugangsdaten-institut.ts`
 * (das Einrichtungswerkzeug hinter `D-9`) schon vorher von Hand auflöste.
 * Neu ist der Weg, nicht der Inhalt.
 */
export const fintsInstitutSchema = z
    .object({
    blz: z.string(),
    bic: z.string(),
    institut: z.string(),
    ort: z.string(),
    url: z.string(),
})
    .strict();
/** Die Antwort von `GET /bank/fints/institute?suche=…`. */
export const fintsInstitutListeSchema = z.object({ institute: z.array(fintsInstitutSchema) }).strict();
// ===========================================================================
// Der Zugangstest (Chip F-zugangstest, Spec 10; Auftrag vom 2026-08-25:
// „Beim Speichern sollte direkt versucht werden, die pushTAN abzurufen, um zu
// prüfen, ob der Login geht.")
// ===========================================================================
/**
 * Ein Prüfdialog — Dialoginitialisierung samt etwaiger Freigabe, **ohne**
 * Auftrag (kein `HKCAZ`, kein `HKSAL`). Er beweist, dass Benutzerkennung, PIN
 * und pushTAN zusammenpassen, bevor der erste echte Abruf ansteht.
 *
 * **Dieselbe Zustandsform wie {@link fintsLaufSchema}**, absichtlich: Es ist
 * derselbe Wartemechanismus (entkoppelte Freigabe mit den Zeiten der Bank,
 * oder ein eingetippter Code) und dieselbe Oberflächenlage — laufend, wartet
 * auf Freigabe, fertig, abgebrochen.
 */
export const fintsZugangstestStartSchema = z.object({ pin: bankPin }).strict();
/**
 * Ein TAN-Verfahren, wie der Zugangstest es meldet.
 *
 * **`name` und `art` sind `null`, wenn nur die Kennzahl bekannt ist.** Das ist
 * kein Rückgabefehler, sondern die ehrliche Auskunft aus einer **abgelehnten**
 * Anmeldung: Deren Rückmeldung `3920` trägt nur Kennzahlen (`BankAnswer.params`),
 * die Bankparameterdaten mit den Namen (`HIBPA`/`HITANS`) liest `lib-fints` bei
 * einer Ablehnung gar nicht erst ein (gemessen an `lib-fints@1.5.0`,
 * `initDialogInteraction.js`: `handleResponse` läuft nur bei
 * `success && !requiresTan`). Bei einer **erfolgreichen** Ernte sind beide
 * Felder belegt.
 */
export const fintsZugangstestVerfahrenSchema = z
    .object({
    code: z.number().int(),
    name: z.string().nullable(),
    art: z.enum(['entkoppelt', 'code']).nullable(),
})
    .strict();
/**
 * Der Zustand eines Zugangstests (`GET …/fints/test/:testId`).
 *
 * **Kein Feld trägt eine PIN oder die Produkt-ID** — dieselbe Zusage wie bei
 * {@link fintsLaufSchema} (`E-313`).
 */
export const fintsZugangstestSchema = z
    .object({
    testId: uuid,
    bankAccountId: uuid,
    zustand: fintsLaufZustandSchema,
    /** Der Wortlaut des Instituts, unverändert (Spec 4.2, Schritt 3). */
    aufforderung: z.string().nullable(),
    /** Der Name des TAN-Mediums, zur Anzeige. */
    tanMedium: z.string().nullable(),
    /** Sekunden bis zur nächsten Statusabfrage — **aus den Angaben der Bank**. */
    naechsteFrageIn: z.number().int().nonnegative().nullable(),
    /** Die wievielte Freigabeaufforderung gerade läuft, ab 1. */
    freigabeRunde: z.number().int().positive().nullable(),
    /** Wie viele Runden das Institut zulässt. */
    freigabeRundenMax: z.number().int().positive().nullable(),
    freigabeArt: fintsFreigabeArtSchema.nullable(),
    /** `null`, solange der Test läuft; danach der Ausgang. */
    erfolgreich: z.boolean().nullable(),
    /**
     * Die vom Institut zugelassenen TAN-Verfahren — aus einer erfolgreichen
     * Ernte (BPD/Rückmeldung `3920`) oder, mit unbekanntem Namen und unbekannter
     * Art, aus der Rückmeldung `3920` einer abgelehnten Anmeldung. `null`, wenn
     * nichts dergleichen aufgetreten ist. Sie füttert die Auswahl in F-tanliste
     * und, bei einer Ablehnung, die Auswahl, mit der die Zugangsmaske ein
     * fehlendes TAN-Verfahren nachträgt.
     */
    verfahren: z.array(fintsZugangstestVerfahrenSchema).nullable(),
    /**
     * Die TAN-Medien des **ausgewählten** Verfahrens — wörtlich, wie die Bank
     * sie führt (`HITAB`). `null`, solange kein Verfahren ausgewählt ist
     * (`fints_zugang.tan_verfahren_id`): Die Mediumpflicht hängt am einen
     * Verfahren, nicht am Zugang, und ohne Auswahl gibt es keine Vorbedingung
     * für die Bank, sie zu nennen. Ein zweiter Test — nach einer
     * Verfahrenswahl aus `verfahren` — trägt sie nach.
     */
    /**
     * Der hinterlegte Medienname, den das Institut **nicht anbietet** — das
     * getauschte Telefon (`V-fintsmedium`). Fehlt, solange nichts dergleichen
     * aufgetreten ist.
     *
     * Er steht hier, weil der Zugang unverändert richtig aussieht, während die
     * TAN auf einem anderen Gerät landet: Ohne diese Zeile sucht ein Mensch die
     * Ursache dort, wo sie nicht ist. **Geschwärzt** wie jedes Freitextfeld
     * dieses Vertrags (`E-313`).
     *
     * Eine **leere** Medienliste erzeugt dieses Feld ausdrücklich NICHT — sie
     * ist keine Auskunft der Bank, sondern deren Henne-Ei: `HITAB` kommt erst
     * nach einer erfolgreichen Anmeldung.
     */
    tanMediumNichtAngeboten: z.string().optional(),
    tanMedien: z.array(z.string()).nullable(),
    /**
     * Der **geschwärzte** Mitschnitt der gewechselten Nachrichten, in der
     * Reihenfolge des Wechsels (`V-fintswire`). Fehlt, solange nichts
     * gewechselt wurde.
     *
     * **Nur der Zugangstest führt ihn**, nicht der Abruf: Dort geht ein CAMT
     * über die Leitung, und ein Beleg gehört ins Archiv und nicht in einen
     * Diagnosezustand. Siehe {@link fintsDrahtNachrichtSchema} dazu, warum er
     * kein Geheimnis tragen kann.
     */
    draht: z.array(fintsDrahtNachrichtSchema).optional(),
    fehler: z
        .object({
        code: z.string(),
        meldung: z.string(),
        /**
         * Die Meldung des Wurfs, der hinter dem Code steckt — geschwärzt und
         * auf 500 Zeichen gekürzt (`FI-ursachekette`, `E-1425`; dieselbe Form
         * und dieselbe Begründung wie bei
         * `lastschriftEinreichungLaufSchema.fehler.ursache`,
         * `LS-einreichdiagnose`, `E-1349`).
         *
         * **Warum sie ein eigenes Feld ist und nicht an `meldung` hängt.**
         * `meldung` ist eine der sieben Meldungen aus Spec 9, wörtlich und
         * ohne angehängten Grund — die Zusage aus `E-313`. Was eine fremde
         * Bibliothek sagt, steht deshalb daneben und nicht darin; die
         * Oberfläche zeigt es unter der Meldung, nicht in ihr. `undefined`,
         * solange der Abbruch einen eigenen Code dieses Hauses ohne fremde
         * Ursache trug: Dann gibt es nichts danebenzustellen.
         *
         * **Der Stapelabzug steht ausdrücklich NICHT hier**, sondern nur im
         * Änderungsprotokoll: Er nennt Dateinamen und Zeilen dieses Programms
         * — eine Auskunft für den, der den Fehler behebt, und keine für den
         * Bildschirm dessen, der gerade Umsätze abrufen wollte.
         */
        ursache: z.string().optional(),
        /**
         * Dieselbe Erweiterung wie bei `fintsLaufSchema.fehler.bankRueckmeldungen`
         * (`F-diagnose2`): Code, geschwärzter Institutstext und, wenn
         * auflösbar, Bezugssegment. Fehlt oder ist leer, wenn keine
         * Rückmeldung bekannt ist.
         */
        bankRueckmeldungen: z.array(fintsBankRueckmeldungSchema).optional(),
    })
        .strict()
        .nullable(),
})
    .strict();
/**
 * Der Rumpf des Medienabrufs (`POST …/fints/tan-medien`, `V-fintstab`).
 *
 * **Dieselbe Form wie {@link fintsZugangstestStartSchema}, und trotzdem ein
 * eigener Vertrag.** Ein Endpunkt, dessen Rumpf sich nur zufällig mit einem
 * anderen deckt, teilt keinen Namen mit ihm: Bekäme der Zugangstest je ein
 * zweites Feld, änderte sich sonst still auch dieser hier.
 */
export const fintsTanMedienStartSchema = z.object({ pin: bankPin }).strict();
/**
 * Was der Medienabruf zurückgibt (`V-fintstab`, 2026-08-26) — der Hibiscus-Weg
 * aus dem Henne-Ei der Sparkassen.
 *
 * **Warum es diesen Endpunkt gibt.** Eine Sparkasse weist die Anmeldung mit
 * `9955 „Die Gerätebezeichnung ist unbekannt"` ab, weil deren `HKTAN` die
 * **Anmeldung** stark authentifizieren will und dafür den Namen des TAN-Mediums
 * verlangt. Den nennt das Institut aber erst in `HITAB`, und `HITAB` kommt
 * erst nach einer erfolgreichen Anmeldung. Der Ausweg ist ein Dialog, dessen
 * Init-`HKTAN` sich auf `HKTAB` bezieht — den Vorgang, den die
 * Bankparameterdaten mit `tanRequired = false` führen. Dann verlangt das
 * Institut für diesen Dialog gar keine Freigabe und nennt die Namen.
 *
 * **`freigabeNoetig` ist ein benannter Ausgang und kein Fehler.** Verlangt ein
 * Institut trotz `tanRequired = false` eine Freigabe, ist das eine Auskunft
 * über dieses Institut — der Abruf wartet nicht darauf, weil ein Warten auf
 * eine pushTAN wieder das Henne-Ei wäre.
 *
 * **`draht` trägt kein Geheimnis** — dieselben zwei Schwärzungslagen und
 * derselbe Weg wie beim Zugangstest (`E-313`, `E-316`, `E-359`); siehe
 * {@link fintsDrahtNachrichtSchema}.
 */
export const fintsTanMedienAbrufSchema = z
    .object({
    bankAccountId: uuid,
    /** Ob die Medienliste angekommen ist — `false` auch bei `freigabeNoetig`. */
    erfolgreich: z.boolean(),
    /**
     * Die Gerätebezeichnungen, **wörtlich wie das Institut sie führt** (`HITAB`).
     * Leer, wenn der Abruf nicht durchkam — nie `null`, weil eine leere Liste
     * und „nicht gefragt" hier dasselbe Handeln nach sich ziehen.
     */
    medien: z.array(z.string()),
    /**
     * Die Geräte aus `medien`, die das Institut in `HITAB` als **aktiv** führt
     * (Status 1 „aktiv" oder 3 „aktive Folgekarte"; 2 und 4 sind nur verfügbar),
     * in derselben Reihenfolge (`E-2048`). `null`, wenn die Antwort keinen
     * Status trug oder der Abruf nicht durchkam — „unbekannt" ist nicht „keines
     * aktiv". Die geführte Einrichtung wählt ein einziges aktives Gerät ohne
     * Rückfrage.
     */
    aktiveMedien: z.array(z.string()).nullable(),
    /** Das Institut verlangt eine Freigabe, obwohl `HKTAB` zustimmungsfrei ist. */
    freigabeNoetig: z.boolean(),
    /** Der Wortlaut des Instituts zur Freigabe, unverändert. `null`, wenn keiner kam. */
    aufforderung: z.string().nullable(),
    /** Der **geschwärzte** Mitschnitt der gewechselten Nachrichten (`V-fintswire`). */
    draht: z.array(fintsDrahtNachrichtSchema),
    fehler: z
        .object({
        code: z.string(),
        meldung: z.string(),
        /**
         * Die Meldung des Wurfs, der hinter dem Code steckt — geschwärzt und
         * auf 500 Zeichen gekürzt (`FI-ursachekette`, `E-1425`; dieselbe Form
         * und dieselbe Begründung wie bei
         * `lastschriftEinreichungLaufSchema.fehler.ursache`,
         * `LS-einreichdiagnose`, `E-1349`).
         *
         * **Warum sie ein eigenes Feld ist und nicht an `meldung` hängt.**
         * `meldung` ist eine der sieben Meldungen aus Spec 9, wörtlich und
         * ohne angehängten Grund — die Zusage aus `E-313`. Was eine fremde
         * Bibliothek sagt, steht deshalb daneben und nicht darin; die
         * Oberfläche zeigt es unter der Meldung, nicht in ihr. `undefined`,
         * solange der Abbruch einen eigenen Code dieses Hauses ohne fremde
         * Ursache trug: Dann gibt es nichts danebenzustellen.
         *
         * **Der Stapelabzug steht ausdrücklich NICHT hier**, sondern nur im
         * Änderungsprotokoll: Er nennt Dateinamen und Zeilen dieses Programms
         * — eine Auskunft für den, der den Fehler behebt, und keine für den
         * Bildschirm dessen, der gerade Umsätze abrufen wollte.
         */
        ursache: z.string().optional(),
        bankRueckmeldungen: z.array(fintsBankRueckmeldungSchema).optional(),
    })
        .strict()
        .nullable(),
})
    .strict();
// ===========================================================================
// Die geführte Einrichtung (Chip `F-tanautomatik`, 2026-10-16)
// ===========================================================================
/**
 * Was ein Kunde für die Einrichtung eintippt: Bank, Benutzerkennung, PIN.
 * **Kein TAN-Verfahren und kein TAN-Medium** — beides ermittelt der Lauf aus den
 * Antworten der Bank und fragt nur nach, wenn sie mehrdeutig sind (`E-2015`).
 *
 * **`pinMerken` ist ausdrücklich ein Schalter und keine Vorgabe.** Die PIN lebt
 * für die Dauer des Laufs im Arbeitsspeicher, wie beim Zugangstest. Nur wer
 * `pinMerken: true` sagt, legt sie zusätzlich verschlüsselt am Zugang ab (für
 * den nächtlichen Abruf); ohne den Schalter bleibt eine bereits hinterlegte PIN
 * unverändert stehen, und es wird keine neue hinterlegt.
 */
export const fintsEinrichtungStartSchema = z
    .object({
    url: fintsUrl,
    blz: bankleitzahl,
    benutzerkennung: z.string().trim().min(1, 'Benutzerkennung fehlt').max(200),
    kundenkennung: z.string().trim().min(1).max(200).optional(),
    pin: bankPin,
    pinMerken: z.boolean().optional(),
})
    .strict();
/**
 * Wo die Einrichtung gerade steht. Jeder Wert ist ein benannter Zustand und
 * kein Freitext; die Oberfläche entscheidet an ihm, was sie zeigt.
 *
 * - `laeuft` — die Einrichtung arbeitet (siehe `schritt`).
 * - `wartet_auf_freigabe` — die Bank verlangt eine Freigabe oder TAN.
 * - `verfahren_waehlen` — die Bank nennt mehrere TAN-Verfahren; der Kunde wählt.
 * - `medium_waehlen` — die Bank nennt mehrere Geräte; der Kunde wählt.
 * - `medium_eingeben` — die Bank verlangt ein Gerät, nennt aber keines von
 *   sich aus; der Kunde trägt den Namen ein, wie er im Online-Banking steht.
 * - `kein_verfahren` / `kein_medium` — die Bank nennt nichts; `hinweis` sagt,
 *   was der Kunde bei seiner Bank einrichten muss.
 * - `fertig` / `abgebrochen` — Ende.
 */
export const fintsEinrichtungZustandSchema = z.enum([
    'laeuft',
    'wartet_auf_freigabe',
    'verfahren_waehlen',
    'medium_waehlen',
    'medium_eingeben',
    'kein_verfahren',
    'kein_medium',
    'fertig',
    'abgebrochen',
]);
/** Der Schritt, an dem ein laufender Einrichtungslauf arbeitet. */
export const fintsEinrichtungSchrittSchema = z.enum(['anmeldung', 'verfahren', 'medium', 'pruefung']);
/**
 * Die Wahl des Kunden, wenn die Einrichtung nachgefragt hat: **genau eines** von
 * `verfahrenCode` (aus `verfahrenAuswahl`) oder `medium` (aus `mediumAuswahl`
 * beziehungsweise, im Zustand `medium_eingeben`, frei eingetragen).
 */
export const fintsEinrichtungWahlSchema = z
    .object({
    verfahrenCode: z.number().int().positive().optional(),
    medium: z.string().trim().min(1).max(200).optional(),
})
    .strict()
    .refine((wert) => (wert.verfahrenCode === undefined) !== (wert.medium === undefined), {
    message: 'Es wird genau eines gewählt: ein TAN-Verfahren oder ein TAN-Medium.',
    path: ['verfahrenCode'],
});
/**
 * Der Zustand einer Einrichtung (`GET …/fints/einrichtung/:laufId`).
 *
 * **Kein Feld trägt eine PIN oder die Produkt-ID** (`E-313`). Die Felder zur
 * Freigabe sind die des Zugangstests, der dahinter läuft; `testId` nennt
 * dessen Kennung, damit die Fehlersuche seinen geschwärzten Datenverkehr unter
 * `GET …/fints/test/:testId` findet.
 */
export const fintsEinrichtungSchema = z
    .object({
    laufId: uuid,
    bankAccountId: uuid,
    zustand: fintsEinrichtungZustandSchema,
    /** `null`, sobald der Lauf zu Ende ist oder auf den Kunden wartet. */
    schritt: fintsEinrichtungSchrittSchema.nullable(),
    testId: uuid.nullable(),
    aufforderung: z.string().nullable(),
    tanMedium: z.string().nullable(),
    naechsteFrageIn: z.number().int().nonnegative().nullable(),
    freigabeRunde: z.number().int().positive().nullable(),
    freigabeRundenMax: z.number().int().positive().nullable(),
    freigabeArt: fintsFreigabeArtSchema.nullable(),
    /** Die Verfahren, unter denen der Kunde wählt (Zustand `verfahren_waehlen`). */
    verfahrenAuswahl: z.array(fintsZugangstestVerfahrenSchema).nullable(),
    /** Die Geräte, unter denen der Kunde wählt (Zustand `medium_waehlen`). */
    mediumAuswahl: z.array(z.string()).nullable(),
    /** Das gewählte TAN-Verfahren, sobald es feststeht — von der Bank oder vom Kunden. */
    verfahren: fintsZugangstestVerfahrenSchema.nullable(),
    /** Das gewählte TAN-Medium, sobald es feststeht. */
    medium: z.string().nullable(),
    /** `null`, solange der Lauf läuft; danach der Ausgang. */
    erfolgreich: z.boolean().nullable(),
    /** Ein Satz in Kundensprache zu `kein_verfahren`, `kein_medium` und `medium_eingeben`. */
    hinweis: z.string().nullable(),
    fehler: fintsZugangstestSchema.shape.fehler,
})
    .strict();
//# sourceMappingURL=fints.js.map